import moment from 'moment';
import { List, Map } from 'immutable';
import { afternoonEval, earlyMorningEval, morningEval, nightEval } from './trips/parseTripDetails';
import { resolvePassengerCategory } from './passengerCategories';
import { displayedPriceFromTrip, someTripHasDisplayTotal } from './trips/displayedPrice';

/**
 * Filters the trips based on the provider ID.
 * @param {Immutable.Map} trip - The trip to filter.
 * @param {string} providerId - The ID of the provider.
 * @returns {boolean} Whether the trip matches the provider ID or not.
 */
const providerFilter = (trip, providerId) => !providerId || trip.get('providerId') === providerId;

/**
 * Filters the trips based on the transporter name (group of lines).
 * @param {Immutable.Map} trip - The trip to filter.
 * @param {string} transporterName - The transporter name to match against.
 * @returns {boolean} Whether the trip belongs to the transporter or not.
 */
export const transporterFilter = (trip, transporterName) =>
  !transporterName || trip.getIn(['line', 'transporterName']) === transporterName;

// // flat fare discounts names
// const SINGLE_FLAT_FARE_DISCOUNT = 'single_trip_flat_fare_discount';
// const DEPARTURE_FLAT_FARE_DISCOUNT = 'departure_on_round_trip_flat_fare_discount';
// const SINGLE_ON_ROUND_FLAT_FARE_DISCOUNT = 'single_on_round_trip_flat_fare_discount';

/**
 * Determines whether the arrival-time filter applies at all for the chosen outbound trip.
 *
 * The purchase API skips its "the return must leave after the outbound arrives" validation when
 * either leg is an open ticket, so the funnel skips it too — filtering those out would hide returns
 * that are purchasable today. This is the outbound half of that exemption; `departureAfter` handles
 * the candidate half. It lives here, rather than inline in the returns container, so the exemption
 * the ticket calls the easiest regression to introduce has a test of its own.
 * @param {Object} departureTrip - The chosen outbound trip, as a plain object.
 * @returns {boolean} Whether returns should be filtered by the outbound's arrival time.
 */
export const needsDepartureAfterFilter = (departureTrip) =>
  Boolean(departureTrip && !departureTrip.openTicket);

/**
 * Determines if the trip's departure time is after the provided time.
 * @param {Immutable.Map} trip - The trip to compare.
 * @param {string} departureAfterTime - The time to compare against.
 * @returns {boolean} Whether the trip's departure time is after the provided time or not.
 */
export const departureAfter = (trip, departureAfterTime) => {
  if (trip.get('openTicket')) return true;

  const departureTime = moment(departureAfterTime);
  const tripTime = moment(trip.get('departure'));

  return tripTime.isAfter(departureTime);
};

/**
 * Determines if the trip's departure time satisfies the given schedule condition.
 * @param {number} tripHours - The trip's departure time in hours.
 * @param {string} departureTime - The schedule condition.
 * @returns {boolean} Whether the trip's departure time satisfies the schedule condition or not.
 */
export const getScheduleCondition = (tripHours, departureTime) => {
  switch (departureTime) {
    case 'earlymorning':
      return earlyMorningEval(tripHours);
    case 'morning':
      return morningEval(tripHours);
    case 'afternoon':
      return afternoonEval(tripHours);
    case 'night':
      return nightEval(tripHours);
    default:
      return true;
  }
};

/**
 * Filters the trips based on the departure time.
 * @param {Immutable.Map} trip - The trip to filter.
 * @param {Array|string} departureTimes - The departure times to filter by.
 * @returns {boolean} Whether the trip matches the departure times or not.
 */
export const departureTimeFilter = (trip, departureTimes) => {
  if (trip.get('openTicket')) return true;
  const tripHours = moment(trip.get('departure')).hours();
  let condition;
  if (Array.isArray(departureTimes)) {
    departureTimes.forEach((dpt) => {
      const evaluatedCondition = getScheduleCondition(tripHours, dpt);
      condition = condition ? condition || evaluatedCondition : evaluatedCondition;
    });
    return condition;
  }
  return getScheduleCondition(tripHours, departureTimes);
};

/**
 * Determines if the trip's number of stops satisfies the given stop condition.
 * @param {number} stops - The trip's number of stops.
 * @param {string} type - The stop condition.
 * @returns {boolean} Whether the trip's number of stops satisfies the stop condition or not.
 */
const getStopCondition = (stops, type) => {
  switch (type) {
    case 'multiple':
      return stops >= 1;
    case 'direct':
      return stops === 0;
    default:
      return true;
  }
};

/**
 * Filters the trips based on the number of stops.
 * @param {Immutable.Map} trip - The trip to filter.
 * @param {Array|string} types - The stop types to filter by.
 * @returns {boolean} Whether the trip matches the stop types or not.
 */
export const stopsFilter = (trip, types) => {
  if (trip.get('openTicket')) return true;

  const stops = trip.get('stops');
  let condition;
  if (Array.isArray(types)) {
    types.forEach((type) => {
      const evaluatedCondition = getStopCondition(stops, type);
      condition = condition ? condition || evaluatedCondition : evaluatedCondition;
    });
    return condition;
  }
  return getStopCondition(stops, types);
};

/**
 * Filters the trips based on the passenger categories.
 * @param {Immutable.Map} trip - The trip to filter.
 * @param {Array} categories - The passenger categories to filter by.
 * @returns {boolean} Whether the trip matches the passenger categories or not.
 */
export const categoriesFilter = (trip, categories, searchPassengers) => {
  if (trip.get('openTicket') || categories.length === 0) {
    return true;
  }

  const tripCategories = trip.get('passengerTypes');
  const tripCategoriesMap = tripCategories.reduce((accum, category) => {
    const type = category.get('type');
    const availability = category.get('availability');
    accum[`${type}`] = availability;
    return accum;
  }, {});

  const tripPassengerTypes = Object.keys(tripCategoriesMap).map((type) => ({ type }));
  const resolvedCategories = categories.map((category) => {
    return resolvePassengerCategory(category, tripPassengerTypes);
  });
  const requiredSeats = categories.reduce((counts, category, index) => {
    const resolved = resolvedCategories[index];
    if (resolved && searchPassengers?.[category]) {
      counts[resolved] = (counts[resolved] || 0) + searchPassengers[category];
    }
    return counts;
  }, {});

  return categories.reduce((accum, category, index) => {
    const resolved = resolvedCategories[index];
    let categoriesHasAvailability = tripCategoriesMap[resolved] && tripCategoriesMap[resolved] > 0;

    if (searchPassengers && searchPassengers[category]) {
      categoriesHasAvailability =
        categoriesHasAvailability &&
        searchPassengers[category] &&
        tripCategoriesMap[resolved] >= requiredSeats[resolved];

      return accum !== null ? accum && categoriesHasAvailability : categoriesHasAvailability;
    }
    if (categoriesHasAvailability || (category === 'supportWoman' && trip.get('supportWoman'))) {
      return accum !== null ? accum || true : true;
    }
    return accum || false;
  }, null);
};

/**
 * Checks if a trip's recline matches the selected recline option.
 * @param {Immutable.Map} trip - The trip to check.
 * @param {string} selectedRecline - The selected recline value.
 * @returns {boolean} Whether the trip's recline matches the selected option.
 */
const checkReclineMatch = (trip, selectedRecline) => {
  const tripRecline = trip.get('seatsRecline');

  if (!tripRecline) {
    return false;
  }

  const tripReclinations = tripRecline.split('/').map((r) => r.trim().replace('°', ''));
  return tripReclinations.includes(selectedRecline);
};

/**
 * Filters the trips based on the trip options and recline options.
 * @param {Immutable.Map} trip - The trip to filter.
 * @param {Array} tripOptions - The trip options to filter by.
 * @returns {boolean} Whether the trip matches the trip options or not.
 */
export const tripOptionsFilter = (trip, tripOptions) => {
  if (trip.get('openTicket') || tripOptions.length === 0) {
    return true;
  }

  return tripOptions.reduce((accum, tripOption) => {
    const property = Object.keys(tripOption)[0];
    const value = tripOption[property];
    const tripAttributeValue = trip.get(property);
    const isObject = typeof tripAttributeValue === 'object';

    if (property === 'seatsRecline') {
      return accum || checkReclineMatch(trip, value);
    }

    if (tripAttributeValue === value || (isObject && tripAttributeValue.includes(value))) {
      return accum !== null ? accum || true : true;
    }
    return accum || false;
  }, false);
};

/**
 * Filters a single trip by the provided filters.
 * @param {Immutable.Map} trip - Trip to filter.
 * @param {Immutable.Map} filters - The filters to apply.
 * @returns {boolean} Whether the trip passes the filters or not.
 */
const filterTripsBy = (trip, filters, searchPassengers) => {
  const providerId = filters.get('providerId');
  const transporterName = filters.get('transporterName');
  const departureTime = filters.get('departureTime');
  const stops = filters.get('stops');
  const sameOrigin = filters.get('sameOrigin');

  const categories = filters.get('categories');
  const departureAfterTime = filters.get('departureAfterTime');
  const hasDepartureTime = departureTime && !departureTime?.includes('none');
  const hasCategories = categories && Boolean(categories?.length);
  const hasStops = stops && !stops?.includes('none');
  const tripOptions = filters.get('tripOptions');
  const reclineOptions = filters.get('reclineOptions');
  const allTripOptions = [...(tripOptions || []), ...(reclineOptions || [])];
  const hasTripOptions = Boolean(allTripOptions?.length);
  const hasRedirectSameOrigin = Boolean(sameOrigin);

  if (
    !hasDepartureTime &&
    !hasCategories &&
    !hasStops &&
    !providerId &&
    !transporterName &&
    !departureAfterTime &&
    !hasTripOptions
  ) {
    return true;
  }

  let providerTrip = true;
  let afterDepartureTrip = true;
  let filtersBaseCondition;

  /**
   * As the first step, the trips are filter from departure time and provider ID.
   * departureAfterTime is a date where the trips are filtered from, this is applied for round trips,
   * because the return date should be after the departure date.
   * providerId is the ID of the provider to filter by. This is done for brands that have multiple lines/providers,
   * in this way it is just showed the trips of the selected provider.
   */
  if (departureAfterTime) {
    afterDepartureTrip = departureAfter(trip, departureAfterTime);
  }
  if (providerId) {
    providerTrip = providerFilter(trip, providerId);
  }
  if (transporterName) {
    providerTrip = transporterFilter(trip, transporterName);
  }
  const baseCondition = providerTrip && afterDepartureTrip;

  if (
    (providerId || transporterName || afterDepartureTrip) &&
    !hasDepartureTime &&
    !hasCategories &&
    !hasStops &&
    !hasTripOptions &&
    !hasRedirectSameOrigin
  )
    return baseCondition;

  /**
   * Now all the filters are applied.
   */
  filtersBaseCondition = hasDepartureTime && departureTimeFilter(trip, departureTime);

  if (hasRedirectSameOrigin) {
    filtersBaseCondition =
      (sameOrigin === true && !trip.get('redirectTo')) ||
      trip.getIn(['redirectTo', 'brand']) === sameOrigin;
  }

  if (hasCategories) {
    const categoriesFiltered = categoriesFilter(trip, categories, searchPassengers);
    filtersBaseCondition = hasDepartureTime
      ? filtersBaseCondition && categoriesFiltered
      : categoriesFiltered;
  }

  if (hasStops) {
    const tripsStopsFiltered = stopsFilter(trip, stops);
    const hasFilters = hasCategories || hasDepartureTime;

    filtersBaseCondition = !hasFilters
      ? tripsStopsFiltered
      : filtersBaseCondition && stopsFilter(trip, stops);
  }

  if (hasTripOptions) {
    const tripOptionsFiltered = tripOptionsFilter(trip, allTripOptions);
    const hasFilters = hasCategories || hasDepartureTime || hasStops;

    filtersBaseCondition = !hasFilters
      ? tripOptionsFiltered
      : filtersBaseCondition && tripOptionsFilter(trip, allTripOptions);
  }

  return baseCondition && filtersBaseCondition;
};

/**
 * Retrieves the list of departure and arrival places for the given trips and provider ID.
 * @param {Immutable.List} trips - The list of trips.
 * @param {string} providerId - The ID of the provider.
 * @returns {Object} The departure and arrival places.
 */
export function tripsLocationByProvider(trips, providerId) {
  if (!trips) {
    return {
      departure: [],
      arrival: [],
    };
  }

  const departurePlaces = [
    {
      id: 'none',
      name: 'Cualquier Ubicación',
      type: 'departureLocation',
    },
  ];
  const arrivalPlaces = [
    {
      id: 'none',
      name: 'Cualquier Ubicación',
      type: 'arrivalLocation',
    },
  ];
  const seenInDeparture = {};
  const seenInArrival = {};

  trips.forEach((trip) => {
    if (!providerFilter(trip, providerId)) {
      return;
    }

    const originId = trip.get('originId');
    const destinationId = trip.get('destinationId');

    if (!seenInDeparture[originId]) {
      departurePlaces.push({
        id: originId,
        name: trip.getIn(['origin', 'name']),
        type: 'departureLocation',
      });
      seenInDeparture[originId] = true;
    }

    if (!seenInArrival[destinationId]) {
      arrivalPlaces.push({
        id: destinationId,
        name: trip.getIn(['destination', 'name']),
        type: 'arrivalLocation',
      });
      seenInArrival[destinationId] = true;
    }
  });

  return {
    departure: departurePlaces,
    arrival: arrivalPlaces,
  };
}

/**
 * Sorts the trips based on the provided property.
 * @param {Immutable.Map} a - The first trip.
 * @param {Immutable.Map} b - The second trip.
 * @param {string} property - The property to sort by.
 * @param {Array} newTerminals - The list of new terminals.
 * @param {boolean} [netFeesForDisplay] - Whether this user is in the INS-1288 variant arm, so
 *   the price sort reads the display total instead of the charged total.
 * @returns {number} The comparison result for sorting.
 */
const sortTripsBy = (a, b, property, newTerminals, netFeesForDisplay = false) => {
  if (['departure', 'arrival'].includes(property)) {
    return moment(a.get(property)).unix() - moment(b.get(property)).unix();
  }

  if (property === 'price') {
    // INS-1288: sort by the figure the card actually shows. Netting is not monotonic across
    // trips -- two trips with different charges can swap order -- so sorting on the gross
    // total would order the list by a number the user cannot see. The control arm reads the
    // gross total, which is what its cards show.
    const subs =
      displayedPriceFromTrip(a, netFeesForDisplay) - displayedPriceFromTrip(b, netFeesForDisplay);
    if (subs === 0) return moment(a.get('departure')).unix() - moment(b.get('departure')).unix();
    return subs;
  }
  if (property === 'openTicket') {
    if (a.get(property) === b.get(property)) return 0;
    return a.get(property) ? -1 : 1;
  }
  if (property === 'discount') {
    const aProviderDiscount = a.getIn(['pricing', 'providerDiscount']);
    const bProviderDiscount = b.getIn(['pricing', 'providerDiscount']);
    const aDiscount = aProviderDiscount ? aProviderDiscount.get('total') : 0;
    const bDiscount = bProviderDiscount ? bProviderDiscount.get('total') : 0;

    return bDiscount - aDiscount;
  }

  if (property === 'near_terminals') {
    if (!newTerminals || !newTerminals.length) {
      return 0;
    }
    const aTerminal = newTerminals.find((terminal) => terminal.id === a.get('originId'));
    const bTerminal = newTerminals.find((terminal) => terminal.id === b.get('originId'));

    if (aTerminal && !bTerminal) return 1;
    if (!aTerminal && bTerminal) return -1;
    if (!aTerminal && !bTerminal) return 0;
  }

  return a.get(property) - b.get(property);
};

/**
 * Filters the trips based on the provided filters.
 * @param {Immutable.List} trips - The list of trips to filter.
 * @param {string} providerId - The ID of the provider to filter by.
 * @param {string} sortBy - The property to sort the trips by.
 * @param {Immutable.Map} filters - The filters to apply.
 * @param {Array} newTerminals - The list of new terminals.
 * @param {object} [searchPassengers] - The searched passenger selection, for the category filters.
 * @param {Function} [resolvePriceWithoutFeesVariant] - Reads the INS-1288 A/B assignment, given
 *   whether the filtered list carries anything to reduce. Injected rather than imported so this
 *   module stays a function of its arguments: the real reader reaches the Redux store singleton
 *   and the GrowthBook instance, and importing it made every consumer of `filterTrips` -- and its
 *   own suite -- depend on both (@luisAzcuaga). Defaults to control, so a caller that does not
 *   pass it sorts by the charged total, which is the pre-INS-1288 behaviour.
 * @returns {Immutable.List} The filtered and sorted list of trips.
 */
export function filterTrips(
  trips,
  providerId,
  sortBy,
  filters,
  newTerminals,
  searchPassengers,
  resolvePriceWithoutFeesVariant = () => false,
) {
  if (!trips) return List();
  // eslint-disable-next-line prettier/prettier
  const filteredTrips = filters
    ? trips.filter(
        (trip) => filterTripsBy(trip, filters.merge(Map({ providerId })), searchPassengers),
        // eslint-disable-next-line function-paren-newline
      )
    : trips;

  // INS-1288 A/B: resolved once per sort, never inside the comparator -- the assignment cannot
  // change mid-sort, and GrowthBook would otherwise re-hash on every pair. Gated on a trip
  // actually carrying a displayTotal so a list with nothing to reduce never buckets the user.
  const netFeesForDisplay =
    sortBy === 'price' &&
    Boolean(resolvePriceWithoutFeesVariant(someTripHasDisplayTotal(filteredTrips)));

  const sortedTrips = filteredTrips
    ?.sort((a, b) => sortTripsBy(a, b, sortBy || 'departure', newTerminals, netFeesForDisplay))
    ?.sort((a, b) => sortTripsBy(a, b, 'openTicket'));
  return sortedTrips;
}

/**
 * Utility functions for filter operations.
 */

/**
 * Compares two values for equality, handling both primitive and object types.
 * For objects, performs deep comparison using JSON.stringify.
 * For primitives, uses strict equality (===).
 *
 * @param {*} value1 - First value to compare.
 * @param {*} value2 - Second value to compare.
 * @returns {boolean} True if values are equal, false otherwise.
 */
export const isFilterValueEqual = (value1, value2) => {
  if (typeof value1 === 'object' && typeof value2 === 'object') {
    return JSON.stringify(value1) === JSON.stringify(value2);
  }
  return value1 === value2;
};

/**
 * Finds a filter option in an array by value, handling both primitive and object types.
 * For objects, performs deep comparison using JSON.stringify.
 * For primitives, uses strict equality (===).
 *
 * @param {Array} options - Array of filter options to search in.
 * @param {*} value - The value to search for.
 * @param {string} valueKey - The key to access the value property (defaults to 'value').
 * @returns {Object|undefined} The found option or undefined if not found.
 */
export const findFilterOptionByValue = (options, value, valueKey = 'value') => {
  if (!options || !Array.isArray(options)) {
    return undefined;
  }

  const isObject = typeof value === 'object';

  return options.find((option) => {
    const optionValue = option[valueKey];

    if (isObject) {
      return JSON.stringify(optionValue) === JSON.stringify(value);
    }

    return optionValue === value;
  });
};

/**
 * Checks if a filter option is active/selected in a list of selected options.
 * Handles both primitive and object values.
 *
 * @param {*} value - The filter option value to check.
 * @param {Array} selectedOptions - Array of selected options.
 * @returns {boolean|Object} True if active, false if not, or the matching option object.
 */
export const isFilterOptionActive = (value, selectedOptions) => {
  if (!selectedOptions || !Array.isArray(selectedOptions)) {
    return false;
  }

  if (typeof value === 'object') {
    return selectedOptions.find((option) => JSON.stringify(option) === JSON.stringify(value));
  }

  return selectedOptions.includes(value);
};
